import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react'

interface DashboardMapAnchorValue {
  anchor: HTMLDivElement | null
  setAnchor: (el: HTMLDivElement | null) => void
  onMarkerClick: ((id: number) => void) | null
  setOnMarkerClick: (fn: ((id: number) => void) | null) => void
}

const DashboardMapAnchorContext = createContext<DashboardMapAnchorValue | null>(null)

export function DashboardMapAnchorProvider({ children }: { children: ReactNode }) {
  const [anchor, setAnchorState] = useState<HTMLDivElement | null>(null)
  const [onMarkerClick, setOnMarkerClickState] = useState<((id: number) => void) | null>(null)

  // Stable setters. Without useCallback these functions get a new identity on
  // every provider render; consumer useEffects that depend on the setter (e.g.
  // DashboardPage registering its handler) would re-fire infinitely and
  // freeze the app. useCallback fixes that.
  const setAnchor = useCallback((el: HTMLDivElement | null) => {
    setAnchorState(el)
  }, [])

  const setOnMarkerClick = useCallback((fn: ((id: number) => void) | null) => {
    // useState with a function value: the setter must wrap the function in another
    // function, otherwise React treats it as an initializer and calls it immediately.
    setOnMarkerClickState(() => fn)
  }, [])

  // Memo the value so consumers that destructure are stable across unrelated
  // provider re-renders.
  const value = useMemo<DashboardMapAnchorValue>(
    () => ({ anchor, setAnchor, onMarkerClick, setOnMarkerClick }),
    [anchor, setAnchor, onMarkerClick, setOnMarkerClick]
  )

  return (
    <DashboardMapAnchorContext.Provider value={value}>
      {children}
    </DashboardMapAnchorContext.Provider>
  )
}

export function useDashboardMapAnchor() {
  const ctx = useContext(DashboardMapAnchorContext)
  if (!ctx) {
    throw new Error('useDashboardMapAnchor must be used inside DashboardMapAnchorProvider')
  }
  return ctx
}
